import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useAppState } from "@/components/AppStateProvider";
import Box from "@/components/Box";
import Divider from "@/components/Divider";
import RichText from "@/components/RichText";
import { useTestProductModalContext } from "@/contexts/TestProductModalContext";
import { useOrganisationBasketContext } from "@/contexts/organisations/OrganisationBasketContext";
import { useOrganisationContext } from "@/contexts/organisations/OrganisationContext";
import { theme } from "@/core/theme";
import { TOGGLE_TEST_PRODUCT_FAVOURITE_MUTATION } from "@/graphql/accounts";
import { LATEST_SAMPLE_FOR_PATIENT_QUERY } from "@/graphql/shop";
import useUserProfile from "@/hooks/useUserProfile";
import { PanelBoxV2 } from "@/tpo/Boxes";
import { GilroyFontCurrency } from "@/tpo/Currency";
import Group from "@/tpo/Group";
import { Error } from "@/tpo/InputWrapper";
import Grid from "@/components/Grid";
import { BaseRadio } from "@/tpo/Radio";
import SelectPatientField from "@/tpo/SelectPatient";
import Stack from "@/tpo/Stack";
import MainDetails from "@/tpo/partnerDashboard/practitioner/MainDetails";
import BiomarkersAndSymptomsList from "@/tpo/shop/BiomarkersAndSymptomsList";
import Carousel from "@/tpo/shop/Carousel";
import RelatedPdfs from "@/tpo/shop/RelatedFiles";
import RelatedFileClasses from "@/tpo/shop/RelatedFiles.module.css";
import TestProductInformationPanels from "@/tpo/shop/TestProductInformationPanels";
import { DISPLAY_CONTEXT_ALWAYS_VISIBLE, DISPLAY_CONTEXT_B2B_ONLY } from "@/tpo/shop/constants";
import getCarouselImages from "@/tpo/shop/testProductDetail/utils/getCarouselImages";
import { LinkButton } from "@/v2/Buttons";
import { useLazyQuery, useMutation } from "@apollo/client";

import CheckoutPatientDetails from "../../checkout/ui/CheckoutPatientDetails";
import {
  CMP_PERMISSION_PURCHASE_PRODUCTS,
  CYREX_BASE_UNAVAILABLE_FOR_MULTI_PATIENT_BASKET_MESSAGE
} from "../../constants";
import CyrexAddons from "./CyrexAddons";
import CyrexStoredSampleSummary from "./CyrexStoredSampleSummary";

/** Must match `family_name` for the stored-sample option in `setup_cyrex_feature.CYREX_PURCHASE_OPTIONS`. */
const CYREX_STORED_SAMPLE_OPTION_FAMILY_NAME = "Stored sample retrieval";

function isCyrexStoredSampleOption(option) {
  return option?.testProductOptionFamily?.name === CYREX_STORED_SAMPLE_OPTION_FAMILY_NAME;
}

const EMPTY_OPTIONS = [];

function CyrexOptionTradePrice({ tradeCurrentPrice, currencySymbol }) {
  if (tradeCurrentPrice == null || Number(tradeCurrentPrice) === 0) {
    return null;
  }
  return (
    <Box mt="16px" alignSelf="flex-start">
      <GilroyFontCurrency
        value={tradeCurrentPrice}
        color="dark"
        symbol={currencySymbol}
        fontSize={36}
        decimalFontSize={22}
      />
    </Box>
  );
}

function CyrexPurchaseOptionLayout({
  optionId,
  checked,
  onChange,
  disabled,
  label,
  familyDescription,
  tradeCurrentPrice,
  currencySymbol,
  children
}) {
  const color = disabled ? "midGrey" : "green";
  return (
    <Grid gridTemplateColumns="auto 1fr" gridRowGap={0} pt={40}>
      <BaseRadio
        name="purchase-option"
        value={optionId}
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        color={color}
      />
      <Stack gap={0} gridColumnStart="2">
        <Box
          fontFamily="gilroyBold"
          fontSize={[24]}
          lineHeight="36px"
          alignSelf="flex-start"
        >
          {label}
        </Box>
        {familyDescription ? (
          <Box mt="8px" fontFamily="gilroyMedium" fontSize={[16]} lineHeight="28px">
            <RichText>{familyDescription}</RichText>
          </Box>
        ) : null}
        <CyrexOptionTradePrice
          tradeCurrentPrice={tradeCurrentPrice}
          currencySymbol={currencySymbol}
        />
        {children}
      </Stack>
    </Grid>
  );
}

function findOrgPatient(patients, selectId) {
  if (selectId == null || selectId === "" || !patients?.length) {
    return undefined;
  }
  return patients.find(p => String(p.id) === String(selectId));
}

/** Parent line for this test product — match `id` or `productCode` on either side. */
function findBasketProductLine(lineItems, testProduct) {
  if (!lineItems?.length || !testProduct) {
    return null;
  }
  const byModalId = lineItems.find(li => String(li.product?.id) === String(testProduct.id));
  if (byModalId) {
    return byModalId;
  }
  const code = testProduct.productCode;
  if (code != null && code !== "") {
    return (
      lineItems.find(li => String(li.product?.id) === String(code)) ??
      lineItems.find(li => String(li.product?.productCode) === String(code)) ??
      null
    );
  }
  return null;
}

export default function CyrexProductDetail() {
  const { testProduct, selectTestProductId, selectTestProductOptionId } =
    useTestProductModalContext();
  const isStudentAllowed = !!testProduct?.isStudentAllowed;
  const studentRestrictionMessage = "Not available";

  const { organisation } = useOrganisationContext();

  const {
    basket,
    addProductToBasket,
    addOnToProduct,
    removeAddOnFromProduct,
    setProductQtyInOrganisationBasket,
    setCyrexProductOptionForBasket,
    loading: basketLoading
  } = useOrganisationBasketContext();

  const basketRef = useRef(basket);
  basketRef.current = basket;

  const { setPractitionerBasketOpen: setBasketOpen } = useAppState();
  const permissions = organisation?.permissions || [];

  const carouselImages = getCarouselImages(testProduct);

  const isCyrexBundle =
    testProduct.requiredAddons != null && testProduct.requiredAddons.length > 0;

  const handleAddToBasket = (product, option) => {
    if (!product?.isStudentAllowed) {
      return;
    }
    if (basket?.multiplePatientsEnabled && !basketProductLine) {
      return;
    }
    addProductToBasket(product.id, option?.id);
    if (basket?.multiplePatientsEnabled) {
      setBasketOpen(true);
      selectTestProductId(null);
      selectTestProductOptionId(null);
    }
  };

  const practitionerSupportRequired = !permissions.includes(CMP_PERMISSION_PURCHASE_PRODUCTS);

  const [localSelectedOptionId, setLocalSelectedOptionId] = useState(null);
  const [selectedPatientId, setSelectedPatientId] = useState("");

  const [
    fetchSample,
    { data: sampleData, loading: sampleLoading, reset: resetLatestSampleQuery }
  ] = useLazyQuery(LATEST_SAMPLE_FOR_PATIENT_QUERY, {
    fetchPolicy: "network-only"
  });

  const lastBasketSampleInitKeyRef = useRef(null);
  const prevTestProductIdRef = useRef(undefined);

  const basketProductLine = useMemo(
    () => findBasketProductLine(basket?.productLineItems, testProduct),
    [basket?.productLineItems, testProduct]
  );

  const blockAddingCyrexBase = Boolean(
    basket?.multiplePatientsEnabled && !basketProductLine
  );

  const purchaseOptions = testProduct.options ?? EMPTY_OPTIONS;

  const effectiveSelectedOptionId = useMemo(() => {
    if (
      localSelectedOptionId != null &&
      basketProductLine &&
      String(localSelectedOptionId) !== String(basketProductLine.productOption?.id)
    ) {
      return localSelectedOptionId;
    }
    if (basketProductLine?.productOption?.id) {
      return basketProductLine.productOption.id;
    }
    if (localSelectedOptionId != null) {
      return localSelectedOptionId;
    }
    return null;
  }, [basketProductLine, localSelectedOptionId]);

  const isPurchaseOptionChecked = optionId =>
    effectiveSelectedOptionId != null &&
    String(effectiveSelectedOptionId) === String(optionId);

  const selectedOption = useMemo(
    () =>
      purchaseOptions.find(o => String(o.id) === String(effectiveSelectedOptionId)) ?? null,
    [purchaseOptions, effectiveSelectedOptionId]
  );

  const handlePurchaseOptionChange = useCallback(
    optionId => {
      const b = basketRef.current;
      if (!b?.id || !organisation?.id) {
        return;
      }
      const line = findBasketProductLine(b.productLineItems, testProduct);
      if (b.multiplePatientsEnabled && !line) {
        return;
      }
      setLocalSelectedOptionId(optionId);
      const option = (testProduct.options ?? EMPTY_OPTIONS).find(
        o => String(o.id) === String(optionId)
      );
      let patientPk;
      if (
        option &&
        isCyrexStoredSampleOption(option) &&
        selectedPatientId &&
        organisation?.patients
      ) {
        patientPk = findOrgPatient(organisation.patients, selectedPatientId)?.patientProfile?.pk;
      }

      if (line?.product?.id) {
        const cyrexOptionPayload =
          patientPk != null && patientPk !== "" ? { patient: patientPk } : {};
        setCyrexProductOptionForBasket(line.product.id, optionId, cyrexOptionPayload);
      } else {
        const addKwargs =
          patientPk != null && patientPk !== "" ? { patientId: patientPk } : {};
        addProductToBasket(testProduct.id, optionId, undefined, addKwargs);
      }
    },
    [
      organisation,
      testProduct,
      selectedPatientId,
      setCyrexProductOptionForBasket,
      addProductToBasket
    ]
  );

  useEffect(() => {
    const productId = testProduct?.id;
    if (!productId) {
      return;
    }

    const prevProductId = prevTestProductIdRef.current;
    if (prevProductId !== undefined && prevProductId !== productId) {
      lastBasketSampleInitKeyRef.current = null;
      setLocalSelectedOptionId(null);
      setSelectedPatientId("");
      resetLatestSampleQuery?.();
    }
    prevTestProductIdRef.current = productId;

    if (!basket?.productLineItems?.length || !organisation?.patients?.length) {
      return;
    }

    const line = findBasketProductLine(basket.productLineItems, testProduct);
    if (!line) {
      return;
    }

    const lineOptionId = line.productOption?.id;
    const lineOption = testProduct?.options?.find(o => String(o.id) === String(lineOptionId));
    if (!lineOptionId || !isCyrexStoredSampleOption(lineOption)) {
      return;
    }

    const sample = line?.originalSampleOrderTestItem;
    const sampleUserId = sample?.registeredUser?.id;
    const basketPatientUserId = basket?.patient?.user?.id;

    const userId = sampleUserId || basketPatientUserId;
    if (!userId) {
      return;
    }

    let orgPatient = organisation.patients.find(
      p => String(p.patientProfile?.user?.id) === String(userId)
    );
    if (!orgPatient?.id && basket?.patient?.pk != null) {
      orgPatient = organisation.patients.find(
        p => String(p.patientProfile?.pk) === String(basket.patient.pk)
      );
    }
    if (!orgPatient?.id) {
      return;
    }

    const initKey = `${productId}-${line.id}-${lineOptionId}-${userId}-${sample?.id ?? ""}`;
    if (lastBasketSampleInitKeyRef.current === initKey) {
      return;
    }
    lastBasketSampleInitKeyRef.current = initKey;

    setSelectedPatientId(String(orgPatient.id));
    fetchSample({
      variables: {
        userId,
        organisationId: organisation.id,
        productId
      }
    });
  }, [
    testProduct,
    testProduct?.id,
    testProduct?.options,
    basket?.productLineItems,
    basket?.patient?.user?.id,
    basket?.patient?.pk,
    organisation?.id,
    organisation?.patients,
    fetchSample,
    resetLatestSampleQuery
  ]);

  const handlePatientSelect = patientId => {
    setSelectedPatientId(patientId != null && patientId !== "" ? String(patientId) : "");
    if (patientId) {
      const patient = findOrgPatient(organisation?.patients, patientId);
      const userId = patient?.patientProfile?.user?.id;
      if (userId) {
        fetchSample({
          variables: {
            userId,
            organisationId: organisation.id,
            productId: testProduct.id
          }
        });
      }
    }

    const storedOption = purchaseOptions.find(isCyrexStoredSampleOption);
    const line = findBasketProductLine(basket?.productLineItems, testProduct);
    if (
      !storedOption ||
      effectiveSelectedOptionId == null ||
      String(effectiveSelectedOptionId) !== String(storedOption.id)
    ) {
      return;
    }

    if (!patientId) {
      resetLatestSampleQuery?.();
      if (line?.product?.id) {
        setCyrexProductOptionForBasket(testProduct.id, storedOption.id, {
          clearBasketPatient: true
        });
      }
      return;
    }

    const patientProfilePk = findOrgPatient(organisation?.patients, patientId)?.patientProfile
      ?.pk;
    if (!patientProfilePk) {
      return;
    }
    if (basket?.multiplePatientsEnabled && !line) {
      return;
    }
    if (line?.product?.id) {
      setCyrexProductOptionForBasket(testProduct.id, storedOption.id, {
        patient: patientProfilePk
      });
    } else {
      addProductToBasket(testProduct.id, storedOption.id, undefined, {
        patientId: patientProfilePk
      });
    }
  };

  const latestSample =
    selectedPatientId && sampleData?.latestSampleForPatient
      ? sampleData.latestSampleForPatient
      : null;

  const selectedPatient =
    selectedPatientId && organisation?.patients
      ? findOrgPatient(organisation.patients, selectedPatientId)
      : null;

  const options = !isCyrexBundle ? (
    <Box mt={[60, 60, 80]}>
      <Box fontFamily="gilroyBold" fontSize={[28]} lineHeight="36px">
        Purchase Options
      </Box>
      {blockAddingCyrexBase ? (
        <Box
          color="error"
          fontFamily="gilroyMedium"
          fontSize={[14, 14, 16]}
          mt={20}
          data-testid="cyrexBaseUnavailableForMultiPatientBasket"
        >
          {CYREX_BASE_UNAVAILABLE_FOR_MULTI_PATIENT_BASKET_MESSAGE}
        </Box>
      ) : null}
      <Stack gap={0}>
        {purchaseOptions.map(option => {
          const isStored = isCyrexStoredSampleOption(option);
          const familyDescription = option.testProductOptionFamily?.description;
          const label = option.testProductOptionFamily?.name ?? "Option";

          if (isStored) {
            return (
              <CyrexPurchaseOptionLayout
                key={option.id}
                optionId={option.id}
                checked={isPurchaseOptionChecked(option.id)}
                onChange={handlePurchaseOptionChange}
                disabled={basketLoading || basket?.purchaseAsStock || blockAddingCyrexBase}
                label={label}
                familyDescription={familyDescription}
                tradeCurrentPrice={option.tradeCurrentPrice}
                currencySymbol={basket?.currencySymbol}
              >
                {basket?.purchaseAsStock && (
                  <Box
                    color="error"
                    fontFamily="gilroyMedium"
                    fontSize={[14, 14, 16]}
                    mt="8px"
                    data-testid="cyrexStoredOptionUnavailableForStock"
                  >
                    Stored sample retrieval is not available when ordering as stock.
                  </Box>
                )}
                {isPurchaseOptionChecked(option.id) ? (
                  <Stack gap={[40]} mt={20}>
                    <SelectPatientField
                      patients={organisation?.patients || []}
                      value={selectedPatientId}
                      onChange={handlePatientSelect}
                    />
                    {selectedPatient && (
                      <CheckoutPatientDetails
                        patient={selectedPatient}
                        permissions={permissions}
                      />
                    )}
                    {sampleLoading && (
                      <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]} color="midGrey">
                        Loading sample information...
                      </Box>
                    )}
                    {selectedPatientId &&
                      !sampleLoading &&
                      (!latestSample || !latestSample.isValidForCyrexSample) && (
                        <Error
                          error="You have no stored samples for this patient that are valid for use. Please select an alternative option to proceed."
                          fontSize={[14, 14, 16]}
                        />
                      )}
                    {latestSample && !sampleLoading && (
                      <CyrexStoredSampleSummary sample={latestSample} />
                    )}
                  </Stack>
                ) : null}
              </CyrexPurchaseOptionLayout>
            );
          }

          return (
            <CyrexPurchaseOptionLayout
              key={option.id}
              optionId={option.id}
              checked={isPurchaseOptionChecked(option.id)}
              onChange={handlePurchaseOptionChange}
              disabled={basketLoading || blockAddingCyrexBase}
              label={label}
              familyDescription={familyDescription}
              tradeCurrentPrice={option.tradeCurrentPrice}
              currencySymbol={basket?.currencySymbol}
            />
          );
        })}
      </Stack>
    </Box>
  ) : null;

  const informationPanels = testProduct.content.informationPanels.filter(
    panel =>
      panel.displayContext === DISPLAY_CONTEXT_ALWAYS_VISIBLE ||
      panel.displayContext === DISPLAY_CONTEXT_B2B_ONLY
  );

  const onAddToBasket = () => {
    if (blockAddingCyrexBase) {
      return;
    }
    if (isCyrexBundle) {
      handleAddToBasket(testProduct);
      return;
    }
    if (!selectedOption) {
      return;
    }
    handleAddToBasket(testProduct, selectedOption);
  };

  const { patientProfile } = useUserProfile();

  const setQty = useCallback(
    qty => {
      if (isCyrexBundle) {
        setProductQtyInOrganisationBasket({
          productId: testProduct.id,
          quantity: qty
        });
        return;
      }
      setProductQtyInOrganisationBasket({
        productId: testProduct.id,
        productOptionId: basketProductLine?.productOption?.id,
        quantity: qty
      });
    },
    [
      isCyrexBundle,
      setProductQtyInOrganisationBasket,
      testProduct.id,
      basketProductLine?.productOption?.id
    ]
  );

  const requiredAddons = testProduct.requiredAddons;
  const addons = testProduct.addons;
  const onAddingOn = ({ addon }) => {
    addOnToProduct(testProduct.id, addon.id);
  };
  const onRemovingAddon = ({ addon }) => {
    removeAddOnFromProduct(testProduct.id, addon.id);
  };

  const cyrexAddonsHeaderDescription =
    basket?.purchaseAsStock &&
    (isCyrexBundle || (selectedOption && !isCyrexStoredSampleOption(selectedOption)))
      ? "Add-ons unavailable when purchasing products as stock. They can be added to a stock purchase at the point of sale registration."
      : undefined;

  const cyrexAddonsDisabled = !basketProductLine ||
      !effectiveSelectedOptionId ||
      basket?.purchaseAsStock ||
      (selectedOption &&
        isCyrexStoredSampleOption(selectedOption) &&
        (!selectedPatientId || !basketProductLine?.originalSampleOrderTestItem));
 
  const originalSampleAddonProductIdsFromBasket =
    selectedOption &&
    isCyrexStoredSampleOption(selectedOption) &&
    basketProductLine?.originalSampleOrderTestItem?.cyrexAddonProducts
      ? basketProductLine.originalSampleOrderTestItem.cyrexAddonProducts.map(p => p.id)
      : [];

  const displayTradeCurrentPrice = isCyrexBundle
    ? testProduct.tradeCurrentPrice
    : (selectedOption?.tradeCurrentPrice ?? testProduct.tradeCurrentPrice);
  const displayTradeFullPrice = isCyrexBundle
    ? testProduct.tradeFullPrice
    : (selectedOption?.tradeFullPrice ?? testProduct.tradeFullPrice);
  const displayExvatFullPrice = isCyrexBundle
    ? testProduct.exvatFullPrice
    : (selectedOption?.exvatFullPrice ?? testProduct.exvatFullPrice);
  const showMainTradePrice =
    displayTradeCurrentPrice != null && Number(displayTradeCurrentPrice) !== 0;

  const sampleReports = [...testProduct.sampleLabReports, ...testProduct.sampleGeneratedReports];

  const [toggleFavouriteMutation] = useMutation(TOGGLE_TEST_PRODUCT_FAVOURITE_MUTATION);

  const toggleFavourite = useCallback(
    testProductId => {
      toggleFavouriteMutation({
        variables: {
          testProductId
        }
      });
    },
    [toggleFavouriteMutation]
  );

  const mainDetailsRef = useRef(null);
  const biomarkersAndSymptomsListRef = useRef(null);
  const informationPanelsRef = useRef(null);
  const downloadFilesRef = useRef(null);

  return (
    <>
      <Box display="none" data-component-name="TruncatedProductDetail" />
      <Group
        height={[43.2, 43.2, 131.2]}
        mt={[-43.2, -43.2, -131.2]}
        p={40}
        pr={[80, 80, 88]}
        gap={20}
        alignItems="center"
        justifyContent="center"
        flexWrap="wrap"
      >
        <LinkButton
          active
          sx={{ letterSpacing: "2.8px" }}
          onClick={() => mainDetailsRef.current?.scrollIntoView({ behavior: "smooth" })}
        >
          Overview
        </LinkButton>
        <Divider color="dark" vertical height="10px" />
        <LinkButton
          sx={{ letterSpacing: "2.8px" }}
          onClick={() =>
            biomarkersAndSymptomsListRef.current?.scrollIntoView({ behavior: "smooth" })
          }
        >
          Biomarkers
        </LinkButton>
        <Divider color="dark" vertical height="10px" />
        <LinkButton
          sx={{ letterSpacing: "2.8px" }}
          onClick={() =>
            informationPanelsRef.current?.scrollIntoView({ behavior: "smooth" })
          }
        >
          Test Details
        </LinkButton>
        <Divider color="dark" vertical height="10px" />
        <LinkButton
          sx={{ letterSpacing: "2.8px" }}
          onClick={() => downloadFilesRef.current?.scrollIntoView({ behavior: "smooth" })}
        >
          Downloads
        </LinkButton>
      </Group>
      <PanelBoxV2
        ref={mainDetailsRef}
        maxWidth={768}
        outer={{
          bg: "white",
          pt: carouselImages.length ? 0 : theme.spacing.section.pt,
          pb: theme.spacing.section.pb,
          px: [20, 20, 40]
        }}
        stacked
        gap={[20, 20, 40]}
      >
        {!!carouselImages.length && (
          <Box bg="haze" p={20} mb={20}>
            <Carousel images={carouselImages} bg="haze" />
          </Box>
        )}
        <Box data-component-name="TestProductDetail:MainDetails">
          <MainDetails
            status={testProduct.status}
            practitionerSupportRequired={practitionerSupportRequired}
            onTogglingFavourite={e => {
              e.stopPropagation();
              toggleFavourite(testProduct.id);
            }}
            favourite={patientProfile?.testProductFavouriteIds?.includes(testProduct.id)}
            name={testProduct.name}
            description={testProduct.content.description}
            categories={testProduct.content.categories.map(cat => cat.name)}
            sampleTypes={testProduct.sampleTypes}
            numOfBiomarkersTested={testProduct.content.numOfBiomarkersTested}
            processingTime={testProduct.content.processingTime}
            options={options}
            priceElement={
              <Stack>
                {showMainTradePrice ? (
                  <Group gap={10}>
                    {displayTradeFullPrice !== undefined &&
                      displayTradeFullPrice > displayTradeCurrentPrice && (
                        <GilroyFontCurrency
                          symbol={basket?.currencySymbol}
                          value={displayTradeFullPrice}
                          color="dark"
                          strikethrough
                          data-testid="truncatedProductDetail:tradeFullPrice"
                        />
                      )}
                    <GilroyFontCurrency
                      symbol={basket?.currencySymbol}
                      value={displayTradeCurrentPrice}
                      data-testid="truncatedProductDetail:tradeCurrentPrice"
                      color={
                        displayTradeFullPrice !== undefined &&
                        displayTradeFullPrice > displayTradeCurrentPrice
                          ? "red"
                          : "dark"
                      }
                    />
                  </Group>
                ) : null}
                {showMainTradePrice ? (
                  <GilroyFontCurrency
                    symbol={basket?.currencySymbol}
                    prefix="RRP"
                    color="midGrey"
                    fontSize={18}
                    decimalFontSize={18}
                    value={displayExvatFullPrice}
                    data-testid="truncatedProductDetail:retailFullPrice"
                  />
                ) : null}
              </Stack>
            }
            productOptionCardStackBg="white"
            testType={testProduct.content.testType}
            onAddToBasket={onAddToBasket}
            setQty={setQty}
            centrifuge={testProduct.content.centrifuge}
            addToBasketDisabled={
              !isStudentAllowed ||
              (!isCyrexBundle && !effectiveSelectedOptionId) ||
              blockAddingCyrexBase
            }
            addToBasketDisabledMessage={
              !isStudentAllowed
                ? studentRestrictionMessage
                : blockAddingCyrexBase
                  ? CYREX_BASE_UNAVAILABLE_FOR_MULTI_PATIENT_BASKET_MESSAGE
                  : !isCyrexBundle && !effectiveSelectedOptionId
                    ? "Please select a purchase option"
                    : undefined
            }
          />
        </Box>
      </PanelBoxV2>
      {!!requiredAddons?.length && (
        <CyrexAddons addons={requiredAddons} readOnly />
      )}
      {!!addons?.length && (
        <CyrexAddons
          addons={addons}
          disabled={cyrexAddonsDisabled}
          originalSampleAddonProductIds={originalSampleAddonProductIdsFromBasket}
          headerDescription={cyrexAddonsHeaderDescription}
          onRemovingAddon={onRemovingAddon}
          onAddingOn={onAddingOn}
        />
      )}
      {!!(
        testProduct.content.relatedSymptoms?.length || testProduct.content.biomarkersTested?.length
      ) && (
        <PanelBoxV2
          ref={biomarkersAndSymptomsListRef}
          maxWidth={1020}
          outer={{
            bg: "partners",
            pt: theme.spacing.section.pt,
            pb: theme.spacing.section.pb,
            px: [20, 20, 40],
            "data-component-name": "TestProductDetail:BiomarkersAndSymptomsList"
          }}
        >
          <BiomarkersAndSymptomsList
            relatedSymptoms={testProduct.content.relatedSymptoms}
            biomarkersTested={testProduct.content.biomarkersTested}
          />
        </PanelBoxV2>
      )}
      <PanelBoxV2
        ref={informationPanelsRef}
        maxWidth={768}
        outer={{
          bg: "haze",
          pt: theme.spacing.section.pt,
          px: [20, 20, 40],
          "data-component-name": "TestProductDetail:InformationPanels"
        }}
        stacked
        gap={40}
      >
        <TestProductInformationPanels panels={informationPanels} />
      </PanelBoxV2>
      {!!(
        sampleReports?.length ||
        testProduct.instructions?.length ||
        testProduct.supportingDocuments?.length
      ) && (
        <PanelBoxV2
          ref={downloadFilesRef}
          maxWidth={768}
          outer={{
            bg: "white",
            pt: theme.spacing.section.pt,
            pb: theme.spacing.section.pb,
            px: [20, 20, 40],
            className: RelatedFileClasses.container,
            "data-component-name": "TestProductDetail:DownloadFiles"
          }}
        >
          <Box fontFamily="gilroyBold" fontSize={[28, 28, 36]}>
            Download files
          </Box>
          {!!sampleReports?.length && (
            <RelatedPdfs sectionName="Sample Reports" pdfs={sampleReports} />
          )}
          {!!testProduct.instructions?.length && (
            <RelatedPdfs sectionName="Instructions" pdfs={testProduct.instructions} />
          )}
          {!!testProduct.supportingDocuments?.length && (
            <RelatedPdfs
              sectionName="Supporting Documents"
              pdfs={testProduct.supportingDocuments}
            />
          )}
        </PanelBoxV2>
      )}
    </>
  );
}
